import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { Grid } from '@mui/material';
import { Info } from '@mui/icons-material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Typography, Card, IconButton } from '@totalsoft/rocket-ui';
import HeaderLinks from './header/HeaderLinks';
import DocumentSignStateTransitionsComponent from './DocumentSignStateTransitionsComponent';
import { useParams } from 'hooks';
import { OfferStateCodesForSignatureCancellation } from 'constants/paramCodes';
import { useChangeTrackingLens, sequence, get } from '@totalsoft/change-tracking-react';
import { isDirty } from '@totalsoft/change-tracking';
import { over } from '@totalsoft/rules-algebra-react';
import { append, remove } from 'ramda';
import { shape, required, concatFailure, items, unique } from '@totalsoft/pure-validations';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { isValid, getErrors, useValidation } from '@totalsoft/pure-validations-react';
import * as queries from '../queries';
import * as mutations from '../mutations';
import * as subscriptions from '../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import WrappedFakeText from 'components/common/WrappedFakeText';

const newRow = {
  startStatus: null,
  inProgressStatus: null,
  endStatus: null,
  isPersisted: false,
  isAdded: true
};

const documentSignStateTransitionsValidator = concatFailure(
  unique(x => x.startStatus || {}, 'DocumentSignStateTransitionRules.Validators.StartStatusUnique'),
  items(shape({ startStatus: required, inProgressStatus: required, endStatus: required }))
);

const DocumentSignStateTransitionsContainer = () => {
  const [t] = useTranslation();
  const addToast = useToast();

  const [documentStates, setDocumentStates] = useState([]);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens();
  const [validation, validate] = useValidation(documentSignStateTransitionsValidator);

  const offerStateCodesForSignatureCancellation = useParams(OfferStateCodesForSignatureCancellation);

  useQuery(queries.DOCUMENT_STATES, {
    onCompleted: data => {
      setDocumentStates(data.documentStates);
    }
  });

  useQuery(queries.DOCUMENT_SIGN_STATE_TRANSITIONS, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      resetLens(data.documentSignStateTransitions.map(item => ({ ...item, isPersisted: true, isAdded: false })));
    }
  });

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [lens, dirtyInfo, validate]);

  const handleAddItem = useCallback(() => {
    over(lens, append(newRow));
  }, [lens]);

  const [updateDocumentSignStateTransition] = useMutation(mutations.UPDATE_DOCUMENT_SIGN_STATE_TRANSITION);

  const [deleteDocumentSignStateTransition] = useMutation(mutations.DELETE_DOCUMENT_SIGN_STATE_TRANSITION);

  useSubscription(subscriptions.DOCUMENT_SIGN_STATE_TRANSITION_DELETED, {
    onData: () => {
      addToast(t('DocumentSignStateTransitionEvents.Deleted'), 'success');
    }
  });

  useSubscription(subscriptions.DOCUMENT_SIGN_STATE_TRANSITION_UPDATED, {
    onData: () => {
      addToast(t('DocumentSignStateTransitionEvents.Updated'), 'success');
    }
  });

  const onSave = useCallback(
    (itemLens, idx) => {
      validate(lens |> get);
      if (!validation[idx] |> isValid) return;
      const item = { ...(itemLens |> get) };

      const input = JSON.parse(JSON.stringify(item));
      delete input.isPersisted;
      delete input.isAdded;

      const transitions = lens |> get;
      const trasitionToSave = transitions.find(x => x.startStatus === item.startStatus);

      trasitionToSave.isPersisted = true;
      trasitionToSave.isAdded = false;

      resetLens(transitions);

      updateDocumentSignStateTransition({ variables: { input } });
    },
    [updateDocumentSignStateTransition, validate, lens, resetLens, validation]
  );

  const getItemValidity = useCallback(
    (i, idx) => {
      const list = lens |> get;
      const item = i |> get;

      if (list.some(x => x.isPersisted) && item.isAdded) return item.isPersisted;

      const validity = !isDirty(dirtyInfo[idx]) || !((validation[idx] |> isValid) && (validation |> isValid));
      if (item?.isPersisted) return validity ? item?.isPersisted : validity;
      return validity ? item?.isAdded : validity;
    },
    [dirtyInfo, validation, lens]
  );

  const onDelete = useCallback(
    (itemLens, itemIndex) => {
      if (window.confirm(t('DocumentSignStateTransitions.DeleteTransitionQuestion'))) {
        const input = { ...(itemLens |> get) };
        if (!input.isPersisted) over(lens, remove(itemIndex, 1));
        else {
          delete input.isPersisted;
          delete input.isAdded;
          deleteDocumentSignStateTransition({ variables: { input } });
          over(lens, remove(itemIndex, 1));
        }
      }
    },
    [deleteDocumentSignStateTransition, lens, t]
  );

  const getInfoMessage = param => {
    if (param === '*') return t('DocumentSignStateTransitions.MessageAllOffers');
    else return t('DocumentSignStateTransitions.MessageOfferCodes') + param;
  };

  return (
    <Card
      icon={AssignmentIcon}
      iconColor='primary'
      title={t('DocumentSignStateTransitions.Title')}
      actions={<HeaderLinks onAdd={handleAddItem} />}
      subheader={
        lens &&
        (lens |> sequence).length > 0 &&
        (validation ? validation |> getErrors : false) && <Typography color='error'>{validation |> getErrors}</Typography>
      }
    >
      <Grid container spacing={2}>
        {!(lens |> get) && (
          <Grid item xs={12}>
            <WrappedFakeText lines={5} />
          </Grid>
        )}
        {(lens |> sequence).map((item, idx) => (
          <Grid key={idx} item xs={12}>
            <Card>
              <>
                <Grid container justifyContent='flex-end'>
                  <IconButton
                    type='save'
                    size='small'
                    variant='text'
                    color='primary'
                    tooltip={t('Tooltips.Save')}
                    onClick={() => onSave(item, idx)}
                    disabled={getItemValidity(item, idx)}
                  />
                  <IconButton
                    type='delete'
                    size='small'
                    variant='text'
                    color='primary'
                    tooltip={t('Tooltips.Delete')}
                    onClick={() => onDelete(item, idx)}
                  />
                </Grid>
                <Prompt
                  when={isDirty(dirtyInfo)}
                  message={t('DocumentSignStateTransitions.LeavingWithoutSavingDocumentSignStateTransitions')}
                />
                <DocumentSignStateTransitionsComponent item={item} documentStates={documentStates} validation={validation[idx]} />
              </>
            </Card>
          </Grid>
        ))}
        {offerStateCodesForSignatureCancellation && (
          <IconButton
            type='edit'
            fontSize='medium'
            color='transparent'
            tooltip={getInfoMessage(offerStateCodesForSignatureCancellation)}
            disabled
          >
            <Info />
          </IconButton>
        )}
      </Grid>
    </Card>
  );
};

export default DocumentSignStateTransitionsContainer;
